import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Tag, Facebook, Instagram, Share2, MapPin, Star, Heart, Moon, Music, Compass, Ticket, AlertTriangle, CheckCircle, Coffee, Anchor, Sunset } from 'lucide-react';
import { motion } from 'framer-motion';
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton } from 'react-share';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const JinjaNightCrawling = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'The Ultimate Jinja Night Crawling Experience | TravelCord Blog';
  }, []);

  const handleShare = async (platform: string) => {
    const url = window.location.href;
    const title = "The Ultimate Jinja Night Crawling Experience: Bars, Bites & Nile Adventures (2025 Guide)";

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'instagram':
        alert('Follow us on Instagram @travelcord.guide');
        break;
      default:
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
          } catch (error) {
            console.error('Error sharing:', error);
          }
        }
    }
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1470225620780-dba8ba36b745?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80"
            alt="Jinja Nightlife"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/blog" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Blog
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <div className="flex items-center mb-4 space-x-4">
              <img 
                src="https://imgur.com/ugLHrmP.jpg"
                alt="Ankunda Prima"
                className="h-12 w-12 rounded-full border-2 border-white"
              />
              <div>
                <h3 className="text-lg font-medium">Ankunda Prima</h3>
                <p className="text-white/80 text-sm">Nightlife Expert</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="inline-flex items-center bg-purple-500/20 rounded-full px-2.5 py-0.5 text-xs text-purple-400 mb-4 backdrop-blur-sm">
                <Moon className="h-3 w-3 mr-1" />
                Nightlife Guide
              </div>
              
              <h1 className="text-4xl font-bold">The Ultimate Jinja Night Crawling Experience: Bars, Bites & Nile Adventures (2025 Guide)</h1>
              
              <div className="flex flex-wrap gap-4 text-sm text-white/80">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  April 15, 2025
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  12 min read
                </div>
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Jinja, Uganda
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="prose max-w-none">
                <p className="lead text-lg text-gray-600">
                  As the sun dips below the horizon and the Nile River whispers into the night, Jinja transforms from an adventure sports hub into a vibrant playground of neon lights, sizzling street food, and adrenaline-pumping night adventures. Unlike Kampala's cosmopolitan club scene, Jinja offers something entirely different – a perfect blend of riverside relaxation, cultural immersion, and unexpected thrills that make for an unforgettable night out in 2025.
                </p>

                <p>
                  Whether you're a backpacker looking to unwind after a day of white-water rafting, a solo traveler seeking authentic local experiences, or a group of friends ready to dance until dawn, Jinja's nightlife scene has evolved into a must-experience attraction in its own right. Let me guide you through the ultimate Jinja night crawl – from hidden speakeasies to moonlit boat cruises on the mighty Nile.
                </p>

                <h2 id="why-jinja-nightlife">Why Jinja's Nightlife Is Uniquely Special</h2>
                
                <p>
                  What makes Jinja's after-dark scene stand out from other Ugandan cities? It's the perfect storm of:
                </p>

                <ul>
                  <li><strong>Riverside magic</strong> – Many venues offer stunning Nile views and breezy outdoor seating</li>
                  <li><strong>International influence</strong> – A melting pot of backpackers, expats, and locals creates a diverse vibe</li>
                  <li><strong>Adventure spirit</strong> – The same adrenaline that fuels daytime activities spills into the night</li>
                  <li><strong>Authentic local culture</strong> – Experience Ugandan hospitality, music, and cuisine without the tourist markup</li>
                </ul>

                <div className="my-8">
                  <img 
                    src="https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1829&q=80" 
                    alt="Jinja nightlife scene"
                    className="rounded-lg w-full"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    The vibrant nightlife scene along Jinja's Main Street comes alive after dark
                  </p>
                </div>

                <h2 id="top-night-crawl-stops">Top Jinja Night Crawl Stops (2025 Edition)</h2>

                <p>
                  Ready to experience the best of Jinja after dark? Here's your essential night crawl itinerary for 2025:
                </p>

                <h3>1. The Source Café & Bar</h3>
                <p>
                  Start your evening at this legendary riverside establishment that perfectly balances relaxed vibes with energetic nightlife.
                </p>
                <ul>
                  <li><strong>Vibe:</strong> Laid-back riverside terrace that transforms into a buzzing social hub as the night progresses</li>
                  <li><strong>Must-Try:</strong> Their signature "Nile Special" cocktail – a potent blend of local waragi (gin), passion fruit, and secret spices</li>
                  <li><strong>Insider Tip:</strong> Arrive for sunset (around 6:30 PM) to snag the coveted riverside tables before they fill up</li>
                </ul>

                <h3>2. Nile Nest Lounge</h3>
                <p>
                  This 2025 newcomer has quickly established itself as Jinja's premier nightspot for those seeking a more upscale experience.
                </p>
                <ul>
                  <li><strong>Vibe:</strong> Sophisticated lounge with fire pits, plush seating, and ambient lighting</li>
                  <li><strong>Must-Try:</strong> "Jinja Mule" – a spicy twist on the Moscow Mule using locally-distilled gin</li>
                  <li><strong>Insider Tip:</strong> Thursday nights feature live acoustic sets from Uganda's rising music stars</li>
                </ul>

                <h3>3. Bourbon Street Underground</h3>
                <p>
                  This New Orleans-inspired speakeasy is Jinja's best-kept secret – literally hidden beneath an unassuming coffee shop.
                </p>
                <ul>
                  <li><strong>Vibe:</strong> Intimate jazz club atmosphere with brick walls, dim lighting, and vintage decor</li>
                  <li><strong>Must-Try:</strong> Their barrel-aged bourbon selection and craft cocktails</li>
                  <li><strong>Insider Tip:</strong> To enter, you'll need to tell the barista at Aroma Coffee Shop upstairs that you're "looking for the source" – they'll direct you to the hidden entrance</li>
                </ul>

                <h3>4. Nile Beats Rooftop</h3>
                <p>
                  For those looking to dance the night away, this open-air rooftop venue offers the best nightclub experience in Jinja.
                </p>
                <ul>
                  <li><strong>Vibe:</strong> Energetic dance floor with both international hits and Ugandan bangers</li>
                  <li><strong>Must-Try:</strong> "Rooftop Rumble" shot – a layered concoction that changes color as you drink it</li>
                  <li><strong>Insider Tip:</strong> Ladies' Night on Wednesdays offers free entry and complimentary welcome drinks for women before 11 PM</li>
                </ul>

                <div className="bg-purple-50 p-6 rounded-lg my-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Star className="h-5 w-5 text-purple-600 mr-2" />
                    Local's Recommendation
                  </h3>
                  <p className="text-gray-700 italic">
                    "For the most authentic Jinja nightlife experience, don't stick to just one venue. The magic happens when you bar-hop along Main Street, mixing with locals and travelers alike. Start early at Source Café for sunset drinks, then work your way through the other spots as the night progresses."
                  </p>
                  <p className="text-sm text-gray-600 mt-2">– Ankunda Prima, TravelCord Nightlife Guide</p>
                </div>

                <h2 id="nighttime-activities">Nighttime Activities Beyond Bars</h2>

                <p>
                  Jinja's nightlife isn't limited to bars and clubs. Here are some unique after-dark experiences you won't want to miss:
                </p>

                <h3>Moonlit Nile Boat Cruise</h3>
                <p>
                  Perhaps the most magical nighttime experience in Jinja is cruising the Nile under a canopy of stars. Several operators now offer evening boat trips that combine stunning views with entertainment.
                </p>
                <ul>
                  <li><strong>Best Option:</strong> The "Stargazer Cruise" includes a local astronomer who points out constellations visible from the equator</li>
                  <li><strong>Romantic Option:</strong> "Dinner on the Nile" features a private candlelit meal for two as you drift downriver</li>
                  <li><strong>Party Option:</strong> Weekend "Boat Bash" cruises feature DJs, dancing, and an open bar</li>
                </ul>

                <div className="my-8">
                  <img 
                    src="https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1829&q=80" 
                    alt="Nile night boat party"
                    className="rounded-lg w-full"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    The popular Nile night boat parties offer a unique perspective of Jinja after dark
                  </p>
                </div>

                <h3>Night Bungee Jumping</h3>
                <p>
                  For the ultimate adrenaline rush, try bungee jumping over the Nile – at night! Adrift Adventure Company now offers this heart-pounding experience on Friday and Saturday nights.
                </p>
                <ul>
                  <li><strong>Experience:</strong> The 44-meter plunge is illuminated by floodlights, creating a surreal sensation</li>
                  <li><strong>Safety:</strong> Despite the darkness, all safety protocols remain in place with additional lighting systems</li>
                  <li><strong>Bonus:</strong> Jumpers receive a free t-shirt and a shot of liquid courage at the nearby Adrift Bar</li>
                </ul>

                <h3>Jinja Street Food Night Market</h3>
                <p>
                  The newly established night market near the Main Street roundabout is a feast for the senses, offering the best local and international street food in town.
                </p>
                <ul>
                  <li><strong>Must-Try Dishes:</strong></li>
                  <li><strong>Rolex</strong> – Uganda's famous egg and vegetable wrap</li>
                  <li><strong>Nile Perch Skewers</strong> – Fresh fish grilled to perfection</li>
                  <li><strong>Chapati Quesadillas</strong> – A fusion dish combining Ugandan and Mexican influences</li>
                  <li><strong>Jackfruit Tacos</strong> – A vegetarian delight with local jackfruit</li>
                </ul>

                <div className="bg-yellow-50 p-6 rounded-lg my-8 border-l-4 border-yellow-500">
                  <div className="flex">
                    <AlertTriangle className="h-6 w-6 text-yellow-500 mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold text-yellow-800 mb-2">Foodie Alert</h3>
                      <p className="text-yellow-700">
                        The street food market is only open Thursday through Sunday nights from 7 PM to 2 AM. Come hungry, but be aware that popular stalls often sell out by midnight!
                      </p>
                    </div>
                  </div>
                </div>

                <h2 id="practical-tips">Practical Tips & Safety</h2>

                <h3>Getting Around</h3>
                <ul>
                  <li><strong>Boda Bodas (Motorcycle Taxis):</strong> The most common way to get between venues. Use SafeBoda app for reliable drivers with helmets.</li>
                  <li><strong>Tuk-Tuks:</strong> Slightly more expensive than bodas but safer for groups.</li>
                  <li><strong>Walking:</strong> Main Street venues are walkable, but use caution and stay in groups after dark.</li>
                  <li><strong>Night Shuttle:</strong> The new Jinja Night Rider shuttle runs between major hotels and nightlife spots every 30 minutes until 3 AM on weekends.</li>
                </ul>

                <h3>Dress Code</h3>
                <p>
                  Jinja's nightlife is generally casual, but there are some guidelines:
                </p>
                <ul>
                  <li><strong>Casual Venues (Source Café, Street Food Market):</strong> Anything goes – even flip-flops and shorts are fine</li>
                  <li><strong>Mid-Range (Bourbon Street, Nile Nest):</strong> Smart casual – no beachwear or flip-flops</li>
                  <li><strong>Upscale (Nile Beats Rooftop on weekends):</strong> Dress to impress – no sportswear or sandals</li>
                </ul>

                <h3>Safety Tips</h3>
                <ul>
                  <li><strong>Buddy System:</strong> Always stay with at least one friend, especially when moving between venues</li>
                  <li><strong>Drink Awareness:</strong> Watch your drinks being prepared and never leave them unattended</li>
                  <li><strong>Cash Management:</strong> Bring only what you need; most venues don't accept cards</li>
                  <li><strong>Phone Safety:</strong> Keep phones secure; consider a cross-body phone case</li>
                  <li><strong>Emergency Contacts:</strong> Save Jinja Police (0414505946) and your accommodation's number</li>
                </ul>

                <div className="bg-purple-50 p-6 rounded-lg my-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2" />
                    Pro Tip
                  </h3>
                  <p className="text-gray-700">
                    For the safest and most comprehensive Jinja nightlife experience, consider booking a guided night crawl package. These include a local guide, pre-arranged transportation, VIP entry to venues, and often some included drinks and food tastings.
                  </p>
                </div>

                <h2 id="booking-options">Booking Options & Packages</h2>

                <p>
                  Ready to experience Jinja's nightlife without the hassle of planning? TravelCord offers several curated packages for 2025:
                </p>

                <h3>Solo Traveler Pass ($30)</h3>
                <ul>
                  <li>Guided bar crawl to 4 venues</li>
                  <li>2 welcome drinks</li>
                  <li>Street food tasting</li>
                  <li>SafeBoda credits for transportation</li>
                  <li>Skip-the-line entry at all venues</li>
                </ul>

                <h3>Group Package ($25/person, minimum 4 people)</h3>
                <ul>
                  <li>All Solo Traveler benefits</li>
                  <li>Private transportation between venues</li>
                  <li>Reserved seating at Nile Nest Lounge</li>
                  <li>Group photo package</li>
                </ul>

                <h3>Adventure Lovers Bundle ($120)</h3>
                <ul>
                  <li>All Group Package benefits</li>
                  <li>Night bungee jumping experience</li>
                  <li>2-hour private Nile boat party</li>
                  <li>VIP table service at Nile Beats Rooftop</li>
                </ul>

                <div className="bg-ta-green/10 p-6 rounded-lg my-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Ticket className="h-5 w-5 text-ta-green mr-2" />
                    Ready to Experience Jinja's Nightlife?
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Book your Jinja Night Crawling experience now and let our expert local guides show you the best of what this vibrant riverside city has to offer after dark.
                  </p>
                  <Link 
                    to="/Udanga_Nightlife_Ultimate_Guide"
                    className="inline-flex items-center px-4 py-2 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors"
                  >
                    Book Your Night Crawl
                  </Link>
                </div>

                <h2 id="monthly-events">Monthly Special Events (2025 Calendar)</h2>

                <p>
                  Jinja's nightlife scene is constantly evolving, with special events worth planning your visit around:
                </p>

                <ul>
                  <li><strong>January:</strong> New Year's Nile Fest – Three-day music festival along the riverbank</li>
                  <li><strong>February:</strong> Full Moon Boat Parties – Special cruises during the full moon</li>
                  <li><strong>March:</strong> Jinja Beer Festival – Celebrating Uganda's craft beer scene</li>
                  <li><strong>April:</strong> Nile Jazz Nights – Weekly jazz performances at Bourbon Street</li>
                  <li><strong>May:</strong> International DJ Series – Global DJs at Nile Beats Rooftop</li>
                  <li><strong>June:</strong> Riverside Film Screenings – Outdoor movies at Source Café</li>
                  <li><strong>July:</strong> Nile Food Festival – Extended night market with international cuisine</li>
                  <li><strong>August:</strong> Jinja Cultural Nights – Traditional performances and food</li>
                  <li><strong>September:</strong> Oktoberfest Jinja – German-inspired beer celebration</li>
                  <li><strong>October:</strong> Halloween Boat Party – Costume contest on the Nile</li>
                  <li><strong>November:</strong> Acoustic Unplugged Series – Intimate music performances</li>
                  <li><strong>December:</strong> Nile New Year's Eve – The biggest party of the year</li>
                </ul>

                <p>
                  <strong>Follow <a href="https://instagram.com/travelcord.guide" target="_blank" rel="noopener noreferrer" className="text-ta-green hover:underline">@travelcord.guide</a> on Instagram</strong> for the latest updates on special events and promotions!
                </p>

                <h2 id="conclusion">Conclusion: Your Jinja Night Adventure Awaits</h2>

                <p>
                  Jinja's nightlife offers a refreshing alternative to Kampala's more commercial scene. With its unique blend of riverside relaxation, international influence, and authentic Ugandan hospitality, a night out in Jinja should be on every traveler's Uganda bucket list.
                </p>

                <p>
                  Whether you're sipping cocktails with a view of the Nile, dancing under the stars at a rooftop club, or experiencing the thrill of a nighttime bungee jump, Jinja after dark promises memories that will last long after your Ugandan adventure ends.
                </p>

                <p>
                  So, which Jinja night spot are you most excited to visit first? Tag us in your adventures using #JinjaNights and #TravelCordUganda!
                </p>

                <p>
                  For more Uganda travel guides, including our comprehensive <Link to="/blog/kampala-nightlife-guide" className="text-ta-green hover:underline">Kampala Nightlife Guide</Link>, check out our blog.
                </p>
              </div>

              {/* Tags */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-gray-600 mr-2">Tags:</span>
                  {["Jinja nightlife 2025", "Best bars in Jinja Uganda", "Nile night boat party", "Jinja street food at night", "Uganda travel", "Night activities"].map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-purple-100 hover:text-purple-800 transition-colors cursor-pointer"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Share Buttons */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Share this article</h3>
                <div className="flex space-x-4">
                  <FacebookShareButton
                    url={window.location.href}
                    quote="The Ultimate Jinja Night Crawling Experience: Bars, Bites & Nile Adventures (2025 Guide)"
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Facebook className="h-5 w-5 mr-2" />
                    Facebook
                  </FacebookShareButton>
                  <TwitterShareButton
                    url={window.location.href}
                    title="The Ultimate Jinja Night Crawling Experience: Bars, Bites & Nile Adventures (2025 Guide)"
                    className="flex items-center px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Twitter
                  </TwitterShareButton>
                  <WhatsappShareButton
                    url={window.location.href}
                    title="The Ultimate Jinja Night Crawling Experience: Bars, Bites & Nile Adventures (2025 Guide)"
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Share2 className="h-5 w-5 mr-2" />
                    Share
                  </WhatsappShareButton>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Author Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center mb-4">
                    <img 
                      src="https://imgur.com/ugLHrmP.jpg"
                      alt="Ankunda Prima"
                      className="h-16 w-16 rounded-full"
                    />
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold">Ankunda Prima</h3>
                      <p className="text-gray-600">Nightlife Expert</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    Ankunda has been exploring Uganda's nightlife scene for over 6 years and specializes in connecting travelers with authentic local experiences. Her expertise in Jinja's evolving night scene makes her the perfect guide for your after-dark adventures.
                  </p>
                  <Link to="/guides/3" className="w-full py-2 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors block text-center">
                    View Guide Profile
                  </Link>
                </div>

                {/* Table of Contents */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">In This Article</h3>
                  <nav className="space-y-2">
                    <a href="#why-jinja-nightlife" className="block text-gray-600 hover:text-purple-600">Why Jinja's Nightlife Is Special</a>
                    <a href="#top-night-crawl-stops" className="block text-gray-600 hover:text-purple-600">Top Night Crawl Stops</a>
                    <a href="#nighttime-activities" className="block text-gray-600 hover:text-purple-600">Nighttime Activities</a>
                    <a href="#practical-tips" className="block text-gray-600 hover:text-purple-600">Practical Tips & Safety</a>
                    <a href="#booking-options" className="block text-gray-600 hover:text-purple-600">Booking Options</a>
                    <a href="#monthly-events" className="block text-gray-600 hover:text-purple-600">Monthly Events</a>
                    <a href="#conclusion" className="block text-gray-600 hover:text-purple-600">Conclusion</a>
                  </nav>
                </div>

                {/* Related Articles */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    <Link to="/blog/kampala-nightlife-guide" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1829&q=80"
                          alt="Kampala Nightlife Guide"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-purple-600">
                            Kampala Nightlife Guide: Best Clubs, Bars & Entertainment
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">8 min read</p>
                        </div>
                      </div>
                    </Link>
                    <Link to="/blog/uganda-tribes-cultural-experiences" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/5v6lVl8.jpg"
                          alt="Uganda Cultural Experiences"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-purple-600">
                            Cultural Experiences: Meeting Uganda's Tribes
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">9 min read</p>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* CTA */}
                <div className="bg-purple-50 rounded-lg border border-purple-100 p-6">
                  <div className="flex items-center mb-4">
                    <Moon className="h-6 w-6 text-purple-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">Book a Night Crawl</h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    Experience Jinja's vibrant nightlife with our expert guides. VIP access, transportation, and drinks included!
                  </p>
                  <Link 
                    to="/Udanga_Nightlife_Ultimate_Guide"
                    className="block w-full py-2 bg-purple-600 text-white text-center rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    View Nightlife Packages
                  </Link>
                </div>

                {/* Popular Nightlife Experiences */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Popular Nightlife Experiences</h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <Music className="h-5 w-5 text-purple-600" />
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-gray-900">Live Music Venues</h4>
                        <p className="text-xs text-gray-500">Best spots for local and international acts</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <Anchor className="h-5 w-5 text-purple-600" />
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-gray-900">Riverside Bars</h4>
                        <p className="text-xs text-gray-500">Scenic spots with Nile views</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <Coffee className="h-5 w-5 text-purple-600" />
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-gray-900">Late Night Cafés</h4>
                        <p className="text-xs text-gray-500">Chill spots for night owls</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <Sunset className="h-5 w-5 text-purple-600" />
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-gray-900">Sunset Cruises</h4>
                        <p className="text-xs text-gray-500">Evening boat experiences on the Nile</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default JinjaNightCrawling;